import { computed, getCurrentInstance, inject, reactive } from "vue";
import type { useHeaderMenuData } from "./useHeaderMenuData";
import { canonicalInternalRoute } from "../../../../utils/canonicalPath";
import {
  buildCatalogFacetsInput,
  canonicalCatalogQueryFromLegacy,
  catalogFacetsQuery,
  normalizeCatalogFacets,
  requireOkCatalogSearch,
  type CatalogFacetDescriptor,
} from "../../../../utils/catalogSearch";
import { findMenuChildCategory } from "../../../../utils/menuCategory";
import { bestSellersFilterId } from "../../../../utils/menuFilters";
import { shapeAssetUrl } from "../../../../utils/shapeAssets";
import { FINAL_SALE_CATEGORY_ID } from "../../../../config/finalSale";

export function useHeaderMenuRoutes(
  menuData: ReturnType<typeof useHeaderMenuData>
) {
  const {
    categories,
    manufacturers,
    shapesData,
    colorsData,
    featuresData,
    allFilterGroups,
  } = menuData;

  const normalizeName = (value: string) =>
    String(value || "")
      .toLowerCase()
      .replace(/&amp;/g, "&")
      .replace(/[^a-z0-9]+/g, " ")
      .trim();

  const getFilterSortOrder = (option: any): number | null => {
    const value = Number(option?.filter_sort_order ?? option?.sort_order);
    return Number.isFinite(value) ? value : null;
  };

  const hasVisibleSortOrder = (option: any) => {
    const sortOrder = getFilterSortOrder(option);
    return sortOrder !== null && sortOrder >= 1;
  };

  const isFinalSaleCategory = (category: any) =>
    FINAL_SALE_CATEGORY_ID !== null &&
    String(category?.id) === FINAL_SALE_CATEGORY_ID;

  const isBrandCategory = (category: any) =>
    normalizeName(category?.name).includes("brand");

  const isEyewearCategory = (category: any) => {
    const name = normalizeName(category?.name);
    return name.includes("sunglass") || name.includes("eyeglass");
  };

  const bestSellersCategory = computed(() =>
    findCategoryByName("most popular", "best sellers", "best selling")
  );

  const kidsCategory = computed(() => findCategoryByName("kids"));
  const brandsCategory = computed(() => findCategoryByName("brands"));

  const brandSubcategories = computed(() => {
    const cat = brandsCategory.value;
    if (!cat?.categories) return [];
    return [...cat.categories].sort((a: any, b: any) =>
      String(a.name || "").localeCompare(String(b.name || ""))
    );
  });

  const desktopShopAllCategory = computed(
    () =>
      allCategories.value.find(
        (cat: any) => (cat?.name || "").toLowerCase() === "glasses"
      ) || primaryCategory.value
  );

  const desktopShopAllUrl = computed(() =>
    desktopShopAllCategory.value
      ? toRoute(desktopShopAllCategory.value.url)
      : null
  );

  const desktopBestSellersUrl = computed(
    () => toRoute(bestSellersCategory.value?.url) || "/best-selling-eyewear"
  );

  const desktopMenuItems = computed(() =>
    [
      sunglassesCategory.value,
      eyeglassesCategory.value,
      findCategoryByName("brands"),
    ]
      .filter(Boolean)
      .slice(0, 3)
  );

  const primaryCategory = computed(
    () =>
      categories.value.find((category: any) => isEyewearCategory(category)) ||
      categories.value[0] ||
      null
  );

  const toRoute = (url: string | null | undefined) =>
    canonicalInternalRoute(url);

  const allCategories = computed(() => {
    const result: any[] = [];
    const visit = (items: any[] = []) => {
      items.forEach((item) => {
        result.push(item);
        if (Array.isArray(item?.categories)) visit(item.categories);
      });
    };
    visit(categories.value);
    return result;
  });

  const finalSaleCategory = computed(() =>
    allCategories.value.find((category: any) => isFinalSaleCategory(category))
  );

  const findCategoryByName = (...names: string[]) => {
    // Try each name in priority order (most specific first, broad fallback last)
    for (const rawName of names) {
      const term = normalizeName(rawName);
      const found = allCategories.value.find((category: any) =>
        normalizeName(category?.name).includes(term)
      );
      if (found) return found;
    }
    return undefined;
  };

  const findChildCategoryByName = findMenuChildCategory;

  const sunglassesCategory = computed(
    () => findCategoryByName("sunglasses") || primaryCategory.value
  );

  const eyeglassesCategory = computed(
    () => findCategoryByName("eyeglasses") || primaryCategory.value
  );

  // Facet descriptors of the categories the menu builds filter links for.
  // A legacy `filters=` link makes the catalog page fetch the descriptors
  // before it can fetch the products; a link that already names the facet
  // value costs one request. The menu's filter ids only exist after menu
  // intent, so the descriptors are requested the first time such a link is
  // built and kept for the page session. Until they arrive, or for an id with
  // no single owning facet, the link keeps the legacy id, which the catalog
  // page resolves itself.
  const apollo = getCurrentInstance() ? inject<any>("$vfapollo", null) : null;
  const menuCategoryFacets = reactive<Record<string, CatalogFacetDescriptor[]>>(
    {}
  );
  const requestedFacetCategories = new Set<string>();
  const requestCategoryFacets = (categoryId: string) => {
    if (
      import.meta.env.SSR ||
      !apollo ||
      !categoryId ||
      requestedFacetCategories.has(categoryId)
    ) {
      return;
    }
    requestedFacetCategories.add(categoryId);
    // Never write reactive state while a computed link list is evaluating.
    Promise.resolve()
      .then(() =>
        apollo.query({
          query: catalogFacetsQuery,
          variables: { input: buildCatalogFacetsInput({}, categoryId) },
          fetchPolicy: "no-cache",
        })
      )
      .then((result: any) => {
        const facets = normalizeCatalogFacets(
          requireOkCatalogSearch(result?.data?.catalogSearchV1 || null)
        );
        if (facets.length) menuCategoryFacets[categoryId] = facets;
      })
      .catch(() => {
        // The legacy links stay; they work, one request slower.
      });
  };

  const categoryFilterRoute = (
    category: any,
    filterId: string | number | null | undefined,
    fallbackSearch = ""
  ) => {
    const baseCategory = category || primaryCategory.value;
    if (!baseCategory?.url) return null;
    if (!filterId) {
      return fallbackSearch
        ? categorySearchRoute(baseCategory, fallbackSearch)
        : toRoute(baseCategory.url);
    }
    const categoryId = String(baseCategory.id || "");
    const facets = menuCategoryFacets[categoryId];
    if (!facets) requestCategoryFacets(categoryId);
    const canonicalQuery = facets
      ? canonicalCatalogQueryFromLegacy({ filters: String(filterId) }, facets)
      : null;
    const basePath = toRoute(baseCategory.url)?.path;
    if (canonicalQuery && basePath) {
      return { path: basePath, query: canonicalQuery };
    }
    return canonicalInternalRoute(baseCategory.url, { filters: filterId });
  };

  const categorySearchRoute = (category: any, search: string) => {
    if (!search) return toRoute(category?.url);
    return canonicalInternalRoute(category?.url, { search });
  };

  const newArrivalsRoute = (category: any) =>
    canonicalInternalRoute(category?.url, {
      sort: "date_added",
      order: "DESC",
    });

  const categoryCrossRoute = (parentCategory: any, crossCategory: any) => {
    if (!parentCategory?.url || !crossCategory?.id)
      return toRoute(parentCategory?.url);
    return canonicalInternalRoute(parentCategory.url, {
      category: crossCategory.id,
    });
  };

  const filterByGroup = (groupName: string, optionName: string) => {
    const wantedGroup = normalizeName(groupName);
    const wantedOption = normalizeName(optionName);

    for (const item of allFilterGroups.value as any[]) {
      const group = item?.filter_group;
      if (
        !group ||
        !normalizeName(group.filter_group_name).includes(wantedGroup)
      ) {
        continue;
      }

      const option = group.filter_options?.find((filterOption: any) =>
        normalizeName(filterOption?.filter_name).includes(wantedOption)
      );
      if (option?.filter_id) return option.filter_id;
    }

    return null;
  };

  const optionFilterByGroup = (groupName: string, optionName: string) => {
    const wantedGroup = normalizeName(groupName);
    const wantedOption = normalizeName(optionName);

    for (const item of allFilterGroups.value as any[]) {
      const group = item?.filter_group;
      if (
        !group ||
        !normalizeName(group.filter_group_name).includes(wantedGroup)
      ) {
        continue;
      }

      const option = group.filter_options?.find(
        (filterOption: any) =>
          normalizeName(filterOption?.filter_name) === wantedOption ||
          normalizeName(filterOption?.filter_name).includes(wantedOption)
      );
      if (option?.filter_id) {
        return {
          filter: option.filter_id,
          label: String(option.filter_name || "")
            .replace(/&amp;/g, "&")
            .trim(),
        };
      }
    }

    return null;
  };

  const bestSellersRoute = (category: any) => {
    const resolved = optionFilterByGroup("best sellers", "yes");
    return categoryFilterRoute(category, bestSellersFilterId(resolved?.filter));
  };

  const shapeFilterOverrides: Record<string, number> = {
    oval: 35,
  };

  const shapeFilterByName = (shapeName: string) => {
    const wanted = normalizeName(shapeName === "Aviator" ? "Pilot" : shapeName);
    const apiShape = shapesData.value.find(
      (shape: any) =>
        normalizeName(shape?.name) === wanted ||
        normalizeName(shape?.name).includes(wanted)
    );
    if (apiShape?.filter) {
      const filterId = shapeFilterOverrides[wanted] ?? apiShape.filter;
      return { ...apiShape, filter: filterId, label: shapeName };
    }

    const filter = optionFilterByGroup("frame shape", wanted);
    if (!filter) return null;
    const filterId = shapeFilterOverrides[wanted] ?? filter.filter;
    const image = shapeAssetUrl(wanted) || shapeAssetUrl(shapeName);
    return { ...filter, filter: filterId, image, label: shapeName };
  };

  const colorFilterByName = (colorName: string) => {
    const wanted = normalizeName(colorName);
    const apiColor = colorsData.value.find(
      (color: any) =>
        hasVisibleSortOrder(color) && normalizeName(color?.name) === wanted
    );
    if (apiColor?.filter) return { ...apiColor, label: colorName };

    const filter = optionFilterByGroup("frame color", colorName);
    return filter && hasVisibleSortOrder(filter)
      ? { ...filter, label: colorName }
      : null;
  };

  const menuColorOrder: {
    label: string;
    hex: string;
    match: string[];
    groups: string[];
    multicolor?: boolean;
  }[] = [
    {
      label: "Black",
      hex: "#000000",
      match: ["black"],
      groups: ["frame color", "lens color"],
    },
    {
      label: "Brown",
      hex: "#5C4033",
      match: ["brown"],
      groups: ["frame color", "lens color"],
    },
    {
      label: "Beige / Nude",
      hex: "#D2B48C",
      match: ["beige", "nude"],
      groups: ["frame color"],
    },
    {
      label: "Transparent / Clear",
      hex: "transparent",
      match: ["transparent", "clear"],
      groups: ["frame color", "lens color"],
    },
    {
      label: "White",
      hex: "#FFFFFF",
      match: ["white"],
      groups: ["frame color", "lens color"],
    },
    {
      label: "Silver / Grey / Gunmetal",
      hex: "#A8A8A8",
      match: ["silver", "grey", "gray", "gunmetal"],
      groups: ["frame color", "lens color"],
    },
    {
      label: "Gold",
      hex: "#B8A042",
      match: ["gold"],
      groups: ["frame color", "lens color"],
    },
    {
      label: "Rose Gold",
      hex: "#B76E79",
      match: ["rose gold"],
      groups: ["frame color"],
    },
    {
      label: "Copper",
      hex: "#B87333",
      match: ["copper"],
      groups: ["frame color"],
    },
    {
      label: "Pink",
      hex: "#F4B8CB",
      match: ["pink"],
      groups: ["frame color", "lens color"],
    },
    {
      label: "Violet / Purple",
      hex: "#8B45A6",
      match: ["violet", "purple"],
      groups: ["frame color", "lens color"],
    },
    {
      label: "Red / Bordeaux",
      hex: "#CC0000",
      match: ["red", "bordeaux"],
      groups: ["frame color", "lens color"],
    },
    {
      label: "Blue",
      hex: "#2B5DAA",
      match: ["blue"],
      groups: ["frame color", "lens color"],
    },
    {
      label: "Green",
      hex: "#2EAA57",
      match: ["green"],
      groups: ["frame color", "lens color"],
    },
    {
      label: "Orange",
      hex: "#F5A623",
      match: ["orange"],
      groups: ["frame color", "lens color"],
    },
    {
      label: "Yellow",
      hex: "#F5D76E",
      match: ["yellow"],
      groups: ["frame color", "lens color"],
    },
    {
      label: "Multicolor",
      hex: "multicolor",
      match: ["multicolor", "multi"],
      groups: ["frame color"],
      multicolor: true,
    },
  ];

  const findMenuColorFilter = (matchNames: string[], groupNames: string[]) => {
    for (const groupName of groupNames) {
      const wantedGroup = normalizeName(groupName);
      for (const matchName of matchNames) {
        const wantedColor = normalizeName(matchName);
        for (const item of allFilterGroups.value as any[]) {
          const group = item?.filter_group;
          if (
            !group ||
            !normalizeName(group.filter_group_name).includes(wantedGroup)
          ) {
            continue;
          }
          const option = group.filter_options?.find(
            (filterOption: any) =>
              hasVisibleSortOrder(filterOption) &&
              normalizeName(filterOption?.filter_name) === wantedColor
          );
          if (option?.filter_id) {
            return {
              ...option,
              filter: option.filter_id,
              label: String(option.filter_name || "")
                .replace(/&amp;/g, "&")
                .trim(),
            };
          }
        }
      }
    }

    return null;
  };

  const apiColorByEntry = (entry: (typeof menuColorOrder)[number]) => {
    return colorsData.value.find((color: any) => {
      const colorName = normalizeName(color?.name);
      return entry.match.some(
        (matchName) => colorName === normalizeName(matchName)
      );
    });
  };

  const mobileMenuColors = computed(() =>
    menuColorOrder.map((entry) => {
      const apiColor = apiColorByEntry(entry);
      const filter =
        apiColor?.filter && hasVisibleSortOrder(apiColor)
          ? { ...apiColor, filter: apiColor.filter }
          : findMenuColorFilter(entry.match, entry.groups);

      return {
        key: `color-${filter?.filter || normalizeName(entry.label)}`,
        label: entry.label,
        color: entry.multicolor ? "multicolor" : entry.hex,
        image: filter?.image || apiColor?.image,
        multicolor: entry.multicolor || false,
        type: "color",
        filter: filter?.filter || null,
      };
    })
  );

  const featureLinksForCategory = (category: any) => {
    const apiFeatures = featuresData.value.map((feature: any) => ({
      key: `feature-${feature.filter}`,
      label: feature.name,
      to: categoryFilterRoute(category, feature.filter, feature.name),
    }));

    return apiFeatures.filter((item) => Boolean(item.to));
  };

  const brandFilterLinksForCategory = (category: any) => {
    const brandGroup = (allFilterGroups.value as any[]).find((item: any) =>
      normalizeName(item?.filter_group?.filter_group_name).includes("brand")
    );
    const options = brandGroup?.filter_group?.filter_options || [];

    return options
      .map((brand: any) => ({
        key: `brand-filter-${brand.filter_id}`,
        label: String(brand.filter_name || "").replace(/&amp;/g, "&"),
        to: categoryFilterRoute(category, brand.filter_id),
      }))
      .filter((item: any) => Boolean(item.to));
  };

  const exactShapeNamesForCategory = (category: any) => {
    const isEyeglasses = normalizeName(category?.name).includes("eyeglass");
    return isEyeglasses
      ? [
          "Rectangle",
          "Square",
          "Round",
          "Cat Eye",
          "Oval",
          "Butterfly",
          "Oversized",
          "Pilot",
          "Geometrical",
          "Shield",
          "Wraparound",
          "Ski Mask",
          "Rimless",
        ]
      : [
          "Cat Eye",
          "Round",
          "Butterfly",
          "Rectangle",
          "Square",
          "Oval",
          "Oversized",
          "Pilot",
          "Geometrical",
          "Shield",
          "Wraparound",
          "Ski Mask",
          "Rimless",
        ];
  };

  const exactColorNamesForCategory = (category: any) => {
    return menuColorOrder.map((color) => color.label);
  };

  const exactBrandNamesForCategory = (category: any) => {
    const isEyeglasses = normalizeName(category?.name).includes("eyeglass");
    return isEyeglasses
      ? ["Gucci", "Prada", "Tom Ford", "Saint Laurent", "Miu Miu", "Dior"]
      : [
          "Gucci",
          "Prada",
          "Saint Laurent",
          "Tom Ford",
          "Dior",
          "Ray-Ban",
          "Versace",
        ];
  };

  const brandFilterLinkByName = (category: any, brandName: string) => {
    const normalized = normalizeName(brandName).replace(/\s+/g, "");
    const link = brandFilterLinksForCategory(category).find((brand: any) => {
      const candidate = normalizeName(brand.label).replace(/\s+/g, "");
      return candidate === normalized || candidate.includes(normalized);
    });

    if (link) return { ...link, label: brandName };

    const manufacturer = manufacturers.value.find((brand: any) => {
      const candidate = normalizeName(brand.name).replace(/\s+/g, "");
      return candidate === normalized || candidate.includes(normalized);
    });

    const manufacturerRoute = manufacturer ? toRoute(manufacturer.url) : null;

    return manufacturerRoute
      ? {
          key: `manufacturer-${manufacturer.id}`,
          label: brandName,
          to: manufacturerRoute,
        }
      : {
          key: `brand-search-${brandName}`,
          label: brandName,
          to: categorySearchRoute(category, brandName),
        };
  };

  const mobileRootItems = computed(() => {
    // Exact match on "glasses" — same as the homepage "Shop Now" button
    const shopAllCategory =
      allCategories.value.find(
        (cat: any) => (cat?.name || "").toLowerCase() === "glasses"
      ) || primaryCategory.value;
    const snowGogglesCategory = findCategoryByName(
      "snow goggles",
      "ski goggles"
    );
    const snowGogglesShape = shapeFilterByName("Ski Goggles");

    return [
      {
        key: "shop-all",
        label: "Shop All",
        to: toRoute(shopAllCategory?.url),
      },
      {
        key: "best-sellers",
        label: "Best Sellers",
        to: toRoute(bestSellersCategory.value?.url) || "/best-selling-eyewear",
      },
      {
        key: "sunglasses",
        label: "Sunglasses",
        child: sunglassesCategory.value?.url
          ? {
              key: "category-sunglasses",
              type: "category",
              title: "Sunglasses",
              category: sunglassesCategory.value,
            }
          : null,
      },
      {
        key: "eyeglasses",
        label: "Eyeglasses",
        child: eyeglassesCategory.value?.url
          ? {
              key: "category-eyeglasses",
              type: "category",
              title: "Eyeglasses",
              category: eyeglassesCategory.value,
            }
          : null,
      },
      {
        key: "snow-goggles",
        label: "Snow Goggles",
        to: snowGogglesCategory
          ? toRoute(snowGogglesCategory.url)
          : categoryFilterRoute(
              sunglassesCategory.value,
              snowGogglesShape?.filter,
              "Snow Goggles"
            ),
      },
      {
        key: "kids",
        label: "Kids",
        to: kidsCategory.value ? toRoute(kidsCategory.value.url) : null,
      },
      {
        key: "brands",
        label: "Brands",
        child: { key: "brands", type: "brands", title: "Brands" },
      },
      {
        key: "final-sale",
        label: "Final Sale",
        sale: true,
        to: "/final-sale-eyewear",
      },
    ].filter((item: any) => Boolean(item.child || item.to));
  });

  const sortedManufacturers = computed(() =>
    [...manufacturers.value].sort((a: any, b: any) =>
      String(a.name || "").localeCompare(String(b.name || ""))
    )
  );

  const manufacturersByName = (names: string[]) =>
    names
      .map((name) => {
        const normalized = normalizeName(name).replace(/\s+/g, "");
        return sortedManufacturers.value.find((brand: any) => {
          const candidate = normalizeName(brand.name).replace(/\s+/g, "");
          return candidate === normalized || candidate.includes(normalized);
        });
      })
      .filter(Boolean);

  const featuredBrandNames = [
    "Gucci",
    "Tom Ford",
    "Prada",
    "Miu Miu",
    "Celine",
    "Loewe",
    "Saint Laurent",
    "Dior",
  ];

  const featuredManufacturers = computed(() =>
    featuredBrandNames
      .map((name) => {
        const norm = normalizeName(name).replace(/\s+/g, "");
        return brandSubcategories.value.find(
          (cat: any) =>
            normalizeName(cat.name).replace(/\s+/g, "") === norm ||
            normalizeName(cat.name).replace(/\s+/g, "").includes(norm)
        );
      })
      .filter(Boolean)
  );

  const allBrandsMenuManufacturers = computed(() =>
    manufacturersByName([
      "Balenciaga",
      "Burberry",
      "Celine",
      "Dolce & Gabbana",
      "Fendi",
      "Givenchy",
      "Jimmy Choo",
      "Loewe",
      "Miu Miu",
      "Off-White",
      "Prada Sport",
      "Stella McCartney",
      "Valentino",
    ])
  );

  const brands = computed(() => (data: any[]) => {
    return sortByKey(data, "name");
  });

  const sortByKey = (
    array: any[],
    key: string | number,
    order: "asc" | "desc" = "asc"
  ) =>
    array?.sort((a, b) => {
      const valueA = a[key];
      const valueB = b[key];

      if (valueA < valueB) {
        return order === "asc" ? -1 : 1;
      }
      if (valueA > valueB) {
        return order === "asc" ? 1 : -1;
      }
      return 0;
    });

  return {
    normalizeName,
    getFilterSortOrder,
    hasVisibleSortOrder,
    isFinalSaleCategory,
    isBrandCategory,
    isEyewearCategory,
    finalSaleCategory,
    bestSellersCategory,
    kidsCategory,
    brandsCategory,
    brandSubcategories,
    desktopShopAllCategory,
    desktopShopAllUrl,
    desktopBestSellersUrl,
    desktopMenuItems,
    primaryCategory,
    toRoute,
    allCategories,
    findCategoryByName,
    findChildCategoryByName,
    sunglassesCategory,
    eyeglassesCategory,
    categoryFilterRoute,
    categorySearchRoute,
    bestSellersRoute,
    newArrivalsRoute,
    categoryCrossRoute,
    filterByGroup,
    optionFilterByGroup,
    shapeFilterByName,
    colorFilterByName,
    menuColorOrder,
    mobileMenuColors,
    featureLinksForCategory,
    brandFilterLinksForCategory,
    exactShapeNamesForCategory,
    exactColorNamesForCategory,
    exactBrandNamesForCategory,
    brandFilterLinkByName,
    mobileRootItems,
    sortedManufacturers,
    manufacturersByName,
    featuredManufacturers,
    allBrandsMenuManufacturers,
    brands,
    sortByKey,
  };
}
